import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import styled from 'styled-components/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { Theme } from '@theme/aiaTheme';
import { useTheme } from '@theme/ThemeProvider';
import { Text } from './Text';

type BannerVariant = 'info' | 'success' | 'warning' | 'error';

interface BannerProps {
  variant?: BannerVariant;
  message: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  action?: {
    label: string;
    onPress: () => void;
  };
}

const BannerContainer = styled.View<{ variant: BannerVariant }>`
  flex-direction: row;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md}px;
  background-color: ${({ theme, variant }) => {
    switch (variant) {
      case 'success':
        return theme.colors.successLight;
      case 'warning':
        return theme.colors.warningLight;
      case 'error':
        return theme.colors.errorLight;
      default:
        return theme.colors.infoLight;
    }
  }};
  border-left-width: 4px;
  border-left-color: ${({ theme, variant }) => {
    switch (variant) {
      case 'success':
        return theme.colors.success;
      case 'warning':
        return theme.colors.warning;
      case 'error':
        return theme.colors.error;
      default:
        return theme.colors.info;
    }
  }};
`;

const IconWrapper = styled.View`
  margin-right: ${({ theme }) => theme.spacing.md}px;
`;

const Content = styled.View`
  flex: 1;
`;

const Actions = styled.View`
  flex-direction: row;
  align-items: center;
  margin-left: ${({ theme }) => theme.spacing.md}px;
`;

const ActionButton = styled(TouchableOpacity)`
  padding: ${({ theme }) => theme.spacing.xs}px;
`;

const getIconName = (variant: BannerVariant): string => {
  switch (variant) {
    case 'success':
      return 'check-circle';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'info';
  }
};

const getIconColor = (variant: BannerVariant, theme: Theme): string => {
  switch (variant) {
    case 'success':
      return theme.colors.success;
    case 'warning':
      return theme.colors.warning;
    case 'error':
      return theme.colors.error;
    default:
      return theme.colors.info;
  }
};

export const Banner: React.FC<BannerProps> = ({
  variant = 'info',
  message,
  dismissible = false,
  onDismiss,
  action,
}) => {
  const theme = useTheme();

  return (
    <BannerContainer variant={variant}>
      <IconWrapper>
        <Icon name={getIconName(variant)} size={24} color={getIconColor(variant, theme)} />
      </IconWrapper>
      <Content>
        <Text variant="bodyMedium" color="textPrimary">
          {message}
        </Text>
      </Content>
      <Actions>
        {action && (
          <ActionButton onPress={action.onPress}>
            <Text variant="bodyMedium" color="brandSecondary">
              {action.label}
            </Text>
          </ActionButton>
        )}
        {dismissible && onDismiss && (
          <ActionButton onPress={onDismiss}>
            <Icon name="close" size={20} color={theme.colors.textSecondary} />
          </ActionButton>
        )}
      </Actions>
    </BannerContainer>
  );
};
